// Financial Reports module — ported from legacy mod_job_costs.
// Breadcrumb switcher: Dashboard (Open Jobs & Invoicing, TICKET-22697) and Reports
// (WIP / WIP Value / WIP Movement / Gross Profit / Stock Sales).
// Reports: FastAPI GET /v6/job-costs/reports/* (temporary; flip USE_FASTAPI_REPORTS in useReports.ts)
// Dashboard: /v6/job-costs/dashboard
import { lazy, Suspense, useEffect, useState } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
} from "../components/BreadcrumbDropdownMenu";
import { buildPathLinks } from "../components/breadcrumbPath";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";
import type { AeronetViewProps } from "../types";

const DashboardTab = lazy(() => import("./tabs/DashboardTab"));
const ReportsTab = lazy(() => import("./tabs/ReportsTab"));

/** Match listing header horizontal padding so content lines up with the title. */
const PAGE_PX = { xs: 2, sm: 6 } as const;

// Exported so App.tsx's nav-children registry (src/app/navModeRegistry.ts)
// can derive Finance > Financial Reports' dropdown children from THIS list
// instead of a hand-typed copy in App.tsx. See WP3 recon.
//
// Each item carries its `description` inline — Inventory's own shape — for the
// grey line under the label (BP-85, H76). Read off the tabs: DashboardTab is
// "Open Jobs & Invoicing"; ReportsTab offers WIP, WIP Value/Movement, Gross
// Profit and Stock Sales.
export const breadcrumbMenuItems: { to: string; label: string; description: string }[] = [
  { to: "dashboard", label: "Dashboard", description: "Open jobs and invoicing" },
  { to: "reports", label: "Reports", description: "WIP, profit and sales" },
];

/**
 * Breadcrumb label for a path, resolved from its LAST SEGMENT rather than from
 * a table of whole pathnames.
 *
 * This module is mounted at whatever slug the tenant's nav hands back, so a
 * pathname table cannot enumerate the mounts. `modules.mod_function` is
 * `job_costs` on the GCIP tenants (App.tsx maps `job_costs` -> `/job_costs` for
 * `getAuthProvider() === "gcip"`), while the Cognito products keep the legacy
 * `financereports` href — and g2 carries TWO Finance nav rows on that same
 * `job_costs` function ("Job Costs" 175 and "Financial Reports" 10182). The old
 * table listed only the `financereports` / `financial-reports` spellings, so on
 * g2 every lookup missed and the breadcrumb rendered `undefined`: an empty
 * dropdown trigger above a bare " / ".
 *
 * It also listed `/app/...` keys that can never match — `<BrowserRouter
 * basename="app">` (src/index.tsx) strips `/app` before react-router sees the
 * path, so `location.pathname` is `/job_costs/dashboard`, never
 * `/app/job_costs/dashboard`.
 *
 * Keying on the mode segment makes the header correct at every mount, present
 * and future, instead of correct at the two that happened to be listed.
 */
const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  reports: "Reports",
};

const MODULE_LABEL = "Financial Reports";

export const breadcrumbLabel = (path: string): string => {
  const lastSegment = path.split("/").filter(Boolean).pop() ?? "";
  return MODE_LABELS[lastSegment] ?? MODULE_LABEL;
};

const FinancialReportsLayout: React.FC = () => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "financialReportsBreadcrumbMenu",
  });

  const [pathLinks, setPathLinks] = useState<string[]>([]);

  useEffect(() => {
    setPathLinks(buildPathLinks(location.pathname));
  }, [location.pathname]);

  return (
    <>
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          {pathLinks.map((path) => {
            if (path === location.pathname) {
              return (
                <BreadcrumbHoverRoot key={path} popupState={popupState}>
                  <Button
                    endIcon={<BreadcrumbChevronIcon />}
                    sx={breadcrumbCurrentTriggerSx}
                  >
                    <Typography
                      variant="h2"
                      component="span"
                      sx={breadcrumbCurrentLabelSx}
                    >
                      {breadcrumbLabel(location.pathname)}
                    </Typography>
                  </Button>
                  <BreadcrumbDropdownMenu popupState={popupState}>
                    {breadcrumbMenuItems.map((item) => (
                      <BreadcrumbMenuLinkItem
                        key={item.to}
                        to={item.to}
                        label={item.label}
                        description={item.description}
                        selected={location.pathname.endsWith(item.to)}
                        onClick={popupState.close}
                      />
                    ))}
                  </BreadcrumbDropdownMenu>
                </BreadcrumbHoverRoot>
              );
            }
            return (
              <LinkRouter to={path} key={path}>
                {breadcrumbLabel(path)} /
              </LinkRouter>
            );
          })}
        </Title>
      </BreadcrumbHeaderListing>

      <Box
        sx={
          // Dashboard is an .an-home page and owns its own 48px gutter
          // (aeronet-home.css). Applying PAGE_PX here too would double it to
          // 96px. Other tabs (e.g. Reports) are not .an-home pages and still
          // need this outlet padding to render unchanged.
          location.pathname.endsWith("dashboard")
            ? { textAlign: "left" }
            : {
                px: PAGE_PX,
                pt: { xs: 3, sm: 5 },
                pb: { xs: 4, md: "64px" },
                textAlign: "left",
              }
        }
      >
        <Suspense fallback={<Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />}>
          <Outlet />
        </Suspense>
      </Box>
    </>
  );
};

const FinancialReports: React.FC<AeronetViewProps> = () => {
  return (
    <Box sx={{ backgroundColor: "#fafbfb", minHeight: "100%", textAlign: "left" }}>
      <Routes>
        <Route element={<FinancialReportsLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardTab />} />
          <Route path="reports" element={<ReportsTab />} />
        </Route>
      </Routes>
    </Box>
  );
};

export default FinancialReports;
